import { bindActionCreators } from '@reduxjs/toolkit';
import { space } from 'folio-common-components';
import * as React from 'react';
import { connect } from 'react-redux';
import { Labels } from '../../messages';
import type { Dispatch, RootState } from '../../state';
import type { CompanySearchItem } from '../../state/companySearch';
import {
  type CompanyOwner,
  type CompanySignatory,
  type Person,
  selectors as editSelectors,
  thunks as editThunks,
  newPerson,
} from '../../state/editor';
import { isAllowedOwnerOrgType } from '../../utils';
import { isValidPnum } from '../../utils/validators';
import { CompanyInput } from '../CompanyInput';
import { FormRow } from '../FormRow';
import { PersonInput } from '../PersonInput';
import { Box } from '../box';
import { Button, TextButton } from '../buttons';

interface CompanyFounder {
  name: string;
  orgId: string;
}

interface Props {
  onDone: () => any;
  initialOrgId?: string;
  initialOwner?: CompanyOwner;
  onAddCompanyFounder: (
    company: CompanyFounder,
    signatory: CompanySignatory,
  ) => any;
  onEditCompanyFounder: (
    prevOrgId: string,
    inCompany: CompanyFounder,
    signatory: CompanySignatory,
  ) => any;
  hideCancelButton?: boolean;
}

interface State {
  selectedCompany?: CompanySearchItem;
  person: Person;
  hasClickedSave: boolean;
}

function personToSignatory(p: Person) {
  const { id, name, pNum, phone, email } = p;
  return { id, name, pNum, phone, email } as CompanySignatory;
}

class BaseOwnerCompanyForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { initialOwner } = props;
    this.state = {
      person: initialOwner ? initialOwner.signatories[0] : newPerson(),
      selectedCompany: initialOwner
        ? Object.assign({}, initialOwner.item, {
            street: '',
            postalCode: '',
            incorporation: 'AS',
          })
        : undefined,
      hasClickedSave: false,
    };
  }

  onSave = () => {
    const { person, selectedCompany } = this.state;
    const { onEditCompanyFounder, onDone, onAddCompanyFounder, initialOrgId } =
      this.props;
    if (selectedCompany) {
      if (initialOrgId) {
        onEditCompanyFounder(
          initialOrgId,
          {
            name: selectedCompany.name,
            orgId: selectedCompany.orgId,
          },
          personToSignatory(person),
        );
      } else {
        onAddCompanyFounder(
          {
            name: selectedCompany.name,
            orgId: selectedCompany.orgId,
          },
          personToSignatory(person),
        );
      }
      onDone();
    }
  };

  onDone = () => {
    this.props.onDone();
  };

  render() {
    const { selectedCompany, person, hasClickedSave } = this.state;
    return (
      <Box>
        <div>
          <FormRow>
            <CompanyInput
              onSelect={org => {
                if (org && !isAllowedOwnerOrgType(org.incorporation)) {
                  return;
                }
                this.setState({ selectedCompany: org });
              }}
              selected={selectedCompany}
            />
          </FormRow>
        </div>
        {selectedCompany && (
          <PersonInput
            inputs={['autoName', 'pNum', 'email', 'phone']}
            person={person}
            labelOverrides={{ name: 'Navn på den som skal signere' }}
            disableAutocomplete={true}
            onChange={p => {
              this.setState({ person: { ...person, ...p } });
            }}
            validate={hasClickedSave}
            editType="new"
          />
        )}
        <div css={space([16], 'margin-top')}>
          <Button
            css={space([24], 'margin-right')}
            disabled={!selectedCompany}
            onClick={() => {
              this.setState({ hasClickedSave: true });
              if (!isValidPnum(person.pNum)) {
                return;
              }
              this.onSave();
            }}
          >
            <Labels.Done />
          </Button>{' '}
          {this.props.hideCancelButton ? undefined : (
            <TextButton onClick={this.onDone}>
              <Labels.Cancel />
            </TextButton>
          )}
        </div>
      </Box>
    );
  }
}

// fixme: the any cast there
function mapStateToProps(state: RootState, ownProps: any) {
  return {
    initialOwner: editSelectors.getCompanyOwner(state, ownProps.initialOrgId),
  };
}

// fixme: Sort out the proper thunk that takes into account current
function mapDispatchToProps(dispatch: Dispatch) {
  return bindActionCreators(
    {
      onAddCompanyFounder: editThunks.addCompanyFounder,
      onEditCompanyFounder: editThunks.editCompanyFounder,
    },
    dispatch,
  );
}

export const OwnerCompanyForm = connect(
  mapStateToProps,
  mapDispatchToProps,
)(BaseOwnerCompanyForm);
